const FLUID_OFF =
  (typeof window !== "undefined" && (window as any).__FLUID_OFF__) ||
  localStorage.getItem("FLUID_OFF") === "1";
// MobileLayout.tsx — safe + Fluid + nav qui se cache quand une modale est ouverte
import React, { lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { FluidNavigationContainer } from "../navigation/FluidNavigationContainer";
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { AppDownloadBanner } from "@/components/AppDownloadBanner";
import { DemoBanner } from "@/components/DemoBanner";
import { useAppDownloadBanner } from "@/hooks/useAppDownloadBanner";
import { useViewportHeight } from "@/hooks/useViewportHeight";
import { LocalModalProvider, useLocalModal } from "@/hooks/useLocalModal";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useOptimizedFamily } from "@/hooks/useOptimizedFamily";

// Nav en lazy pour ne jamais casser l'affichage si elle plante
const FluidMobileNavigation = lazy(async () => {
  const mod = await import("../navigation/FluidMobileNavigation");
  return { default: mod.FluidMobileNavigation };
});

// Petit filet anti-crash local
class Boundary extends React.Component<{ children: React.ReactNode }, { error?: any }> {
  state = { error: undefined as any };
  static getDerivedStateFromError(error: any) { return { error }; }
  componentDidCatch(error: any, info: any) { console.error("[BottomNav boundary]", error, info); }
  render() { return this.state.error ? null : this.props.children; }
}

const MobileLayoutInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { currentModal, isModalOpen } = useModalNavigation();
  const { isLocalModalOpen } = useLocalModal();
  const { isVisible: bannerVisible } = useAppDownloadBanner();
  const { family } = useOptimizedFamily();
  const { isDemoActive } = useDemoMode(family?.id);

  // Modales qui doivent masquer la nav (même set que dans ton FluidMobileLayout initial)
  const wrappedModalTypes = new Set([
    "createTask",
    "createTaskList",
    "editTaskList",
    "createEvent",
    "createPerson",
    "createContact",
    "editTask",
    "editEvent",
    "viewPerson",
    "viewTask",
    "viewEvent",
    "editPerson",
    "create_note",
    "create_document",
    "edit_document",
  ]);
  const modalHidesNav = !!(isModalOpen && currentModal.type && wrappedModalTypes.has(currentModal.type)) || isLocalModalOpen;

  // Masquer la navigation sur les pages de paiement et upgrade
  const isUpgradePage = location.pathname.includes('/upgrade') || location.search.includes('view=upgrade');

  // Masquer la navigation seulement quand une modale est ouverte ou sur upgrade
  const navHidden = modalHidesNav || isUpgradePage;

  // Utiliser le système intelligent de viewport
  const { smartPadding } = useViewportHeight(navHidden);

  const APP_BANNER_H = 64; // Height of app download banner
  const DEMO_BANNER_H = 48; // Height of demo banner

  // Padding intelligent basé sur l'état de l'app
  let contentPaddingTop = 0;
  if (bannerVisible) contentPaddingTop += APP_BANNER_H;
  if (isDemoActive) contentPaddingTop += DEMO_BANNER_H;
  
  const contentPaddingBottom = smartPadding;

  return (
    <div
      style={{
        minHeight: "100svh",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        opacity: 1,
        visibility: "visible",
        overflow: "hidden",
      }}
    >
      {/* App Download Banner */}
      <AppDownloadBanner />
      
      {/* Demo Mode Banner */}
      {isDemoActive && family?.id && (
        <div style={{ top: bannerVisible ? `${APP_BANNER_H}px` : '0px' }} className="fixed left-0 right-0 z-50">
          <DemoBanner familyId={family.id} />
        </div>
      )}
      
      <main
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          width: "100%",
          background: "#fff",
          ["--content-pt" as any]: `${contentPaddingTop}px`,
          ["--content-pb" as any]: contentPaddingBottom,
        }}
      >
        <FluidNavigationContainer>{children}</FluidNavigationContainer>
      </main>

      {/* Bottom nav fixe — on la masque quand une modale est ouverte */}
      <Boundary>
        <Suspense fallback={null}>
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              height: `calc(64px + env(safe-area-inset-bottom, 0px))`,
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
              background: "#fff",
              borderTop: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9998,
              // animation de masquage en douceur
              transform: navHidden ? "translateY(100%)" : "translateY(0)",
              opacity: navHidden ? 0 : 1,
              pointerEvents: navHidden ? "none" : "auto",
              transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            aria-hidden={navHidden}
          >
            <FluidMobileNavigation />
          </div>
        </Suspense>
      </Boundary>
    </div>
  );
};

export const MobileLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <LocalModalProvider>
      <MobileLayoutInner>{children}</MobileLayoutInner>
    </LocalModalProvider>
  );
};

export default MobileLayout;